import { useEffect, useState } from 'react'
import I18n from 'translations/i18n'
import ConfirmModal from 'containers/authorised/setting/v2/components/ConfirmModal'
import { useDispatch, useSelector } from 'utils/hooks'
import { loanTypesLoad, loanTypeDelete } from 'redux/payrollOnboarding/Loans/actionCreators'
import {
  Table,
  Badge,
  Flex,
  Spacer,
  SearchBar,
  Checkbox,
  Popper,
  Button,
  Widget,
  Modal,
} from '@jisr-hr/design-system'
import { Typography } from '@jisr-hr/ds'
import { ReactComponent as FilterIcon } from '@jisr-hr/design-system/dist/assets/icons/Filter.svg'
import { ReactComponent as PlusIcon } from '@jisr-hr/design-system/dist/assets/icons/plus-icon.svg'
import { ReactComponent as Star } from 'assets/figma-icons/StarRounded.svg'
import { ReactComponent as EditIcon } from '@jisr-hr/design-system/dist/assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from 'containers/authorised/Performance/Competencies/components/Delete.svg'
import { ReactComponent as HandArmCoinsIcon } from '@jisr-hr/design-system/dist/assets/icons/HandArmCoins.svg'
import { isEmpty } from 'lodash'
import EmptyState from 'containers/authorised/setting/v2/components/EmptyState'
import BadgeWrapper from 'containers/authorised/setting/v2/payroll/Payables/components/BadgeWrapper'
import classNames from 'classnames'
import { organizationInfo } from 'redux/organizationOnborading/organization/actionCreators'
import {
  handelValuesEdit,
  initState,
} from 'containers/authorised/setting/v2/payroll/Payables/components/helper'

import styles from '../style.module.css'
import LoanTypeForm from './LoanTypeForm'

type PayrollTransactionType = {
  default_pay_type: string
  description: string
  id: number
  name_i18n: string
  pay_type: string
  status: string
}

type DataType = {
  id: number
  name_i18n: string
}

type LoansType = { id: number } | null

type ParamsType = {
  filters: {
    default_pay_type: string[]
    status: string[]
  }
  search: string
}

const LoanTypes = (): JSX.Element => {
  const dispatch = useDispatch()
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [obj, setObj] = useState<DataType>({ id: 0, name_i18n: '' })
  const [params, setParams] = useState<ParamsType>(initState)
  const [firstRender, setFirstRender] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [loansType, setLoansType] = useState<LoansType>(null)

  const headers = [
    { label: I18n.t('name') },
    { label: I18n.t('pay_mode') },
    { label: I18n.t('description') },
    { label: I18n.t('status') },
    { label: I18n.t('actions') },
  ]
  const { payroll_transaction_types, fetching, loading } = useSelector(
    ({ onboardingLoans }) => onboardingLoans,
  )

  function handleFilter(data: object | string, key: string): void {
    setParams({ ...params, [key]: data })
    dispatch(loanTypesLoad({ ...params, [key]: data }))
    setFirstRender(false)
  }

  const handleOnChange = (value: string): void => {
    handleFilter(value, 'search')
    setFirstRender(false)
  }

  const handleEdit = (v: Record<string, unknown>): void => {
    setModalOpen(true)
    setLoansType(handelValuesEdit(v) as LoansType)
  }

  const onClose = (): void => {
    setModalOpen(false)
    setLoansType(null)
    dispatch(loanTypesLoad(params))
  }

  const handleDelete = (): void => {
    dispatch(loanTypeDelete(obj.id)).then(() => {
      setOpenConfirmModal(false)
      dispatch(loanTypesLoad(params))
      dispatch(organizationInfo())
    })
  }

  useEffect(() => {
    dispatch(loanTypesLoad(params))
  }, [])

  return (
    <>
      {firstRender && isEmpty(payroll_transaction_types) ? (
        <EmptyState
          icon={<HandArmCoinsIcon />}
          labelBtn={I18n.t('new_loan_types')}
          onClick={(): void => {
            handleEdit({})
          }}
          title={I18n.t('loan_types')}
        />
      ) : (
        <Widget
          className={classNames(styles.widget, styles.widgeDrawer)}
          fetching={fetching}
        >
          <Flex
            justifyBetween
            itemsCenter
            className={styles.title}
          >
            <Typography
              variant="title-1"
              text={I18n.t('loan_types')}
              style={{ color: 'var(--color-base-colors-black-500)' }}
            />

            <Flex style={{ gap: 5 }}>
              <Button
                label={I18n.t('new_loan')}
                startIcon={<PlusIcon className={styles.icon} />}
                size="small"
                variant="secondary"
                onClick={(): void => handleEdit({})}
              />
            </Flex>
          </Flex>
          <div>
            <Spacer height={20} />
            <Flex style={{ gap: 8 }}>
              <Spacer width={8} />
              <SearchBar
                placeholder={I18n.t('search')}
                locale={localStorage.getItem('Locale')}
                onChange={(_: unknown, search: string): void => {
                  handleOnChange(search)
                }}
                value={params.search}
              />
              <Popper
                trigger={
                  <Button
                    icon={<FilterIcon />}
                    size="medium"
                    variant="secondary"
                  />
                }
                className={styles.pay_type_action_popper}
                placement="bottom-end"
                maxHeight={450}
              >
                <Flex
                  flexCol
                  style={{ padding: '10px 20px' }}
                >
                  <Flex
                    flexCol
                    style={{ marginBottom: '8px' }}
                  >
                    <Typography
                      variant="interface/default/sm"
                      text={I18n.t('pay_method')}
                    />
                    <Spacer height="8px" />
                    {['out_of_payrun', 'in_payrun'].map((item: string) => (
                      <Checkbox
                        key={item}
                        label={I18n.t(item)}
                        onChange={(val: boolean): void => {
                          let result = []
                          if (val) {
                            result = [...params.filters.default_pay_type, item]
                          } else {
                            result = params.filters.default_pay_type.filter((it) => it !== item)
                          }
                          handleFilter({ ...params.filters, default_pay_type: result }, 'filters')
                        }}
                        checked={params?.filters?.default_pay_type.includes(item)}
                        size="small"
                      />
                    ))}
                  </Flex>
                  <Flex flexCol>
                    <Typography
                      variant="interface/default/sm"
                      text={I18n.t('status')}
                    />
                    <Spacer height="8px" />
                    {['active', 'inactive'].map((item: string) => (
                      <Checkbox
                        key={item}
                        label={I18n.t(item)}
                        onChange={(val: boolean): void => {
                          let result = []
                          if (val) {
                            result = [...params.filters.status, item]
                          } else {
                            result = params.filters.status.filter((it) => it !== item)
                          }
                          handleFilter({ ...params.filters, status: result }, 'filters')
                        }}
                        checked={params?.filters?.status.includes(item)}
                        size="small"
                        className={styles.pay_type_filter_checkbox}
                      />
                    ))}
                  </Flex>
                </Flex>
              </Popper>
            </Flex>
            <Spacer height={20} />
            <Table
              headers={headers}
              withColumnBorder={false}
              className={styles.table}
            >
              {payroll_transaction_types.map((item: PayrollTransactionType) => (
                <Table.Row
                  key={item.id}
                  className={styles.rtb}
                >
                  <Table.Cell>
                    <Typography
                      variant="interface/default/sm"
                      text={item.name_i18n}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {item.pay_type === 'both' ? (
                      <Flex itemsCenter>
                        <BadgeWrapper
                          data_tip={I18n.t('tool_tip_pay_type_in_payroll')}
                          id={1}
                        >
                          <Badge
                            startIcon={
                              item.default_pay_type === 'in_payrun' && (
                                <Star style={{ width: 17, height: 17 }} />
                              )
                            }
                            label={
                              <Typography
                                variant="interface/default/sm"
                                text={I18n.t('in_payroll')}
                              />
                            }
                            variant={item.default_pay_type === 'in_payrun' ? 'filled' : 'stroke'}
                          />
                        </BadgeWrapper>
                        <Spacer width="8px" />
                        <BadgeWrapper
                          data_tip={I18n.t('tool_tip_pay_type_out_payroll')}
                          id={0}
                        >
                          <Badge
                            startIcon={
                              item.default_pay_type === 'out_of_payrun' && (
                                <Star style={{ width: 17, height: 17 }} />
                              )
                            }
                            label={
                              <Typography
                                variant="interface/default/sm"
                                text={I18n.t('out_of_payroll')}
                              />
                            }
                            variant={
                              item.default_pay_type === 'out_of_payrun' ? 'filled' : 'stroke'
                            }
                          />
                        </BadgeWrapper>
                      </Flex>
                    ) : (
                      <BadgeWrapper
                        id={item.pay_type}
                        data_tip={
                          item.pay_type === 'in_payrun'
                            ? I18n.t('tool_tip_pay_type_in_payroll')
                            : I18n.t('tool_tip_pay_type_out_payroll')
                        }
                      >
                        <Badge
                          label={
                            item.pay_type === 'in_payrun' ? (
                              <Typography
                                variant="interface/default/sm"
                                text={I18n.t('in_payroll')}
                              />
                            ) : (
                              <Typography
                                variant="interface/default/sm"
                                text={I18n.t('out_of_payroll')}
                              />
                            )
                          }
                        />
                      </BadgeWrapper>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Typography
                      variant="interface/default/sm"
                      text={item.description}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      label={I18n.t(item.status)}
                      type="gray"
                      variant="stroke"
                      size="large"
                      bordered
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Flex>
                      <EditIcon
                        className={styles.icon}
                        onClick={(): void => {
                          handleEdit(item)
                        }}
                      />
                      <Spacer width={8} />
                      <DeleteIcon
                        className={styles.icon}
                        onClick={(): void => {
                          setOpenConfirmModal(true)
                          setObj(item)
                        }}
                      />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
              {!fetching && !firstRender && payroll_transaction_types.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Typography
                      variant="subtitle-2"
                      style={{ textAlign: 'center', width: '100%' }}
                      text={I18n.t('no_data')}
                    />
                  </Table.Cell>
                </Table.Row>
              )}
            </Table>
          </div>
        </Widget>
      )}
      <Modal
        header
        disableCloseOut
        isOpen={modalOpen}
        onClose={(): void => {
          setModalOpen(false)
        }}
        width={540}
        content={
          <LoanTypeForm
            loansType={loansType}
            onClose={onClose}
          />
        }
      />
      <ConfirmModal
        fetching={loading}
        variant="danger"
        isOpen={openConfirmModal}
        title={I18n.t('delete_loan_type')}
        message={I18n.t('confirm_message', { name: obj.name_i18n })}
        onCancel={(): void => {
          setOpenConfirmModal(false)
        }}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default LoanTypes
